import React from 'react';
import { Info, Monitor, Mic, Camera, Users } from 'lucide-react';

interface MeetingPreJoinInstructionsProps {
  studentName?: string;
  sessionTitle?: string;
  tutorName?: string;
  className?: string;
}

const MeetingPreJoinInstructions: React.FC<MeetingPreJoinInstructionsProps> = ({
  studentName,
  sessionTitle,
  tutorName,
  className = ""
}) => {
  return (
    <div className={`bg-blue-50 border border-blue-200 rounded-lg p-6 space-y-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center space-x-2 mb-4">
        <Info className="h-6 w-6 text-blue-600" />
        <h3 className="text-lg font-semibold text-blue-900">
          How to Join Your Tutoring Session
        </h3>
      </div>

      {/* Session Info */}
      {(sessionTitle || tutorName || studentName) && (
        <div className="bg-white rounded-md p-3 border border-blue-100">
          {sessionTitle && (
            <p className="text-sm text-gray-700">
              <span className="font-medium">Session:</span> {sessionTitle}
            </p>
          )}
          {tutorName && (
            <p className="text-sm text-gray-700">
              <span className="font-medium">Tutor:</span> {tutorName}
            </p>
          )}
          {studentName && (
            <p className="text-sm text-gray-700">
              <span className="font-medium">You'll join as:</span> <strong>{studentName}</strong>
            </p>
          )}
        </div>
      )}

      {/* Step-by-step instructions */}
      <div className="space-y-3">
        <h4 className="font-medium text-blue-900 mb-2">Step-by-Step Instructions:</h4>
        
        <div className="space-y-2">
          <div className="flex items-start space-x-3">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">1</span>
            <p className="text-sm text-blue-800">Click the "Join Teams Meeting" button below</p>
          </div>

          <div className="flex items-start space-x-3">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">2</span>
            <div className="text-sm text-blue-800">
              <p>If prompted, select <strong>"Join on the web instead"</strong></p>
              <p className="text-xs text-blue-600 mt-1">This will open Teams in your browser</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">3</span>
            <p className="text-sm text-blue-800">Enter your name in the <strong>"Join anonymously"</strong> field</p>
          </div>

          <div className="flex items-start space-x-3">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">4</span>
            <div className="text-sm text-blue-800">
              <p>Allow camera and microphone access when prompted</p>
              <div className="flex items-center space-x-4 mt-1">
                <div className="flex items-center space-x-1">
                  <Camera className="h-3 w-3 text-blue-600" />
                  <span className="text-xs">Camera</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Mic className="h-3 w-3 text-blue-600" />
                  <span className="text-xs">Microphone</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">5</span>
            <p className="text-sm text-blue-800">Click <strong>"Join now"</strong> to enter the session</p>
          </div>
        </div>
      </div>

      {/* Key Benefits */}
      <div className="bg-green-50 border border-green-200 rounded-md p-3">
        <h4 className="font-medium text-green-900 mb-2 flex items-center">
          <Users className="h-4 w-4 mr-1" />
          What You Need to Know:
        </h4>
        <ul className="space-y-1 text-sm text-green-800">
          <li className="flex items-center">
            <span className="text-green-600 mr-2">✓</span>
            No Microsoft account required
          </li>
          <li className="flex items-center">
            <span className="text-green-600 mr-2">✓</span>
            Join directly in your browser
          </li>
          <li className="flex items-center">
            <span className="text-green-600 mr-2">✓</span>
            Full access to video, audio, and chat
          </li>
          <li className="flex items-center">
            <span className="text-green-600 mr-2">✓</span>
            Screen sharing capabilities
          </li>
        </ul>
      </div>

      {/* Troubleshooting Tip */}
      <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
        <div className="flex items-start space-x-2">
          <Monitor className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-amber-900 text-sm">Troubleshooting Tip:</h4>
            <p className="text-xs text-amber-800 mt-1">
              If Teams app keeps trying to open, look for "Join on the web instead" link or try using a different browser.
              Chrome, Firefox, Safari, and Edge all work well.
            </p>
          </div>
        </div>
      </div>

      {/* Technical Requirements */}
      <div className="text-xs text-gray-600 border-t border-blue-100 pt-3">
        <p><strong>Technical Requirements:</strong> Modern web browser with camera and microphone access.
        No software installation required.</p>
      </div>
    </div>
  );
};

export default MeetingPreJoinInstructions;